/**
 * Templates (Stage 3): the organisation's own, then utuwrk's library -- the
 * starters offered from the first day, and the simple, healthcare and defense
 * templates an admin adopts. Each says what it is for, where it is offered,
 * and how much work in flight is on an older version -- the reason to open it.
 * An admin also imports templates from a file here (exported from a
 * template's page, or Export data's Templates file).
 */

import { useRef, useState, type ChangeEvent } from "react";
import { TEMPLATE_GROUPS, type TemplateGroup, type TemplateSummary } from "@utuwrk/core";
import { sentence } from "../lib/api.js";
import { useOrg } from "../lib/org.js";
import { go } from "../lib/router.js";
import { plural } from "../lib/words.js";
import { EmptyState, Link, Loading, PageHeader, Problem, useLoad } from "./bits.js";

/** What a template is for, in a word or two. */
export function purpose(summary: Pick<TemplateSummary, "kind" | "use">): string {
  if (summary.kind === "onboarding") return "Onboarding";
  if (summary.use === "termination") return "Termination";
  if (summary.use === "access-change") return "Access change";
  return "Offboarding";
}

/** Where it is offered, by site name. */
export function whereText(sites: readonly string[], names: ReadonlyMap<string, string>): string {
  if (sites.length === 0) return "Every site";
  return sites.map((id) => names.get(id) ?? "A site that was removed").join(", ");
}

export function Templates() {
  const { api } = useOrg();
  const { data, error } = useLoad(() => Promise.all([api.templates(), api.setup()]), [api]);
  const [busy, setBusy] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const picker = useRef<HTMLInputElement>(null);

  if (!data && !error) return <Loading title="Templates" />;
  const [list, setup] = data ?? [null, null];
  const names = new Map((setup?.levels ?? []).map((level) => [level.id, level.name]));
  const own = list?.templates.filter((template) => !template.starter) ?? [];
  const starters = list?.templates.filter((template) => template.starter) ?? [];

  /** Read the chosen file here and send it whole; the worker checks every template before adding any. */
  async function importFile(event: ChangeEvent<HTMLInputElement>) {
    const chosen = event.target.files?.[0];
    event.target.value = "";
    if (!chosen) return;
    setProblem(null);
    let file: unknown;
    try {
      file = JSON.parse(await chosen.text());
    } catch {
      setProblem(`"${chosen.name}" is not a template file. Choose a .json file exported from utuwrk.`);
      return;
    }
    setBusy("import");
    try {
      const { imported } = await api.importTemplates(file);
      // One template opens; several are listed here, read again.
      if (imported.length === 1) go({ name: "template", id: imported[0]!.id });
      else window.location.reload();
    } catch (failure) {
      setProblem(sentence(failure));
      setBusy(null);
    }
  }

  async function copy(from: string) {
    setBusy(from);
    setProblem(null);
    try {
      const made = await api.copyTemplate(from);
      go({ name: "template", id: made.template.id });
    } catch (failure) {
      setProblem(sentence(failure));
      setBusy(null);
    }
  }

  const row = (template: TemplateSummary) => (
    <li key={template.id}>
      <div className="card template-row">
        <div className="card-top">
          <h2 className="card-title">
            <Link to={{ name: "template", id: template.id }}>{template.name}</Link>
          </h2>
          <span className="card-count">{plural(template.cards, "card")}</span>
        </div>
        <p className="card-meta">
          <span className={`pill ${template.kind === "offboarding" ? "s-warn" : "s-progress"}`}>
            {purpose(template)}
          </span>
          {template.starter ? (
            template.library?.offeredByDefault ? (
              <span className="chip">Offered from the start</span>
            ) : (
              <span className="chip">Offered once you use it</span>
            )
          ) : (
            <span>Version {template.version}</span>
          )}
          {template.starter ? null : <span className="chip">{whereText(template.sites, names)}</span>}
          {template.retired ? <span className="pill s-waiting">Retired</span> : null}
          {template.inFlight > 0 ? <span>{plural(template.inFlight, "in flight", "in flight")}</span> : null}
          {template.behind > 0 && !template.starter ? (
            <span className="pill s-warn">{template.behind} on an older version</span>
          ) : null}
        </p>
        {template.library ? <p className="muted">{template.library.summary}</p> : null}
        <div className="row-actions">
          <Link to={{ name: "template", id: template.id, tab: "preview" }} className="button button-quiet">
            Preview
          </Link>
          {template.starter && list?.canEdit ? (
            <button
              type="button"
              className="button button-quiet"
              disabled={busy !== null}
              onClick={() => void copy(template.id)}
            >
              {busy === template.id
                ? "Copying…"
                : template.library?.offeredByDefault
                  ? "Make a copy to change"
                  : "Use this template"}
            </button>
          ) : null}
        </div>
      </div>
    </li>
  );

  return (
    <section className="page">
      <PageHeader
        title="Templates"
        meta="The cards each onboarding and offboarding is made of. Work already started keeps the version it started with until somebody moves it."
      />
      <Problem>{error ?? problem}</Problem>
      {list ? (
        <>
          <div className="section-row">
            <h2 className="section-title">Your templates</h2>
            {list.canEdit ? (
              <>
                <button
                  type="button"
                  className="button button-quiet"
                  disabled={busy !== null}
                  onClick={() => picker.current?.click()}
                >
                  {busy === "import" ? "Importing…" : "Import from a file"}
                </button>
                <input
                  ref={picker}
                  type="file"
                  accept=".json,application/json"
                  hidden
                  aria-label="Template file to import"
                  onChange={(event) => void importFile(event)}
                />
              </>
            ) : null}
          </div>
          {own.length === 0 ? (
            <EmptyState icon="workflow" title="None of your own yet">
              {list.canEdit
                ? "Use a template from the library below, or make a copy of a starter to change it. Either takes the starter's place at the sites you offer it at."
                : "An admin makes them, from the library below."}
            </EmptyState>
          ) : (
            <ul className="cards">{own.map(row)}</ul>
          )}
          <h2 className="section-title">Template library</h2>
          <p className="muted">
            Ready to use. The four marked "offered from the start" are what work starts from until you choose
            otherwise; using another, such as the healthcare onboarding, puts it in the place of the one it
            varies.
          </p>
          {(Object.keys(TEMPLATE_GROUPS) as TemplateGroup[]).map((group) => {
            const inGroup = starters.filter((template) => template.library?.group === group);
            return inGroup.length ? (
              <section key={group} aria-labelledby={`group-${group}`}>
                <h3 id={`group-${group}`} className="zone-title">
                  {TEMPLATE_GROUPS[group]}
                </h3>
                <ul className="cards">{inGroup.map(row)}</ul>
              </section>
            ) : null;
          })}
        </>
      ) : null}
    </section>
  );
}
